<script lang="ts" setup>
import { QMenu } from 'quasar';
import { FilterOperator } from '~/enums/_types';
import { setNotificationRead } from '~/services/notifications';

const menuRef = ref<QMenu>();
const menuOpen = ref(false);

const notifications = computed(() => data.value?.Notifications || []);

const implicitFilter = [{ column: 'ReadAt', op: FilterOperator.Eq, val: null }];
const { status, hasUnreadNotifications, isLoading, data, request, getActions } = useNotifications(
    {
        rowsPerPage: 5,
        sortBy: 'CreatedAt',
        descending: true,
    },
    implicitFilter
);

watch(isLoading, () => {
    nextTick(() => {
        menuRef.value?.updatePosition();
    });
});

const onMenuBeforeShow = async () => {
    await request();
    nextTick(() => {
        menuRef.value?.updatePosition();
    });
};

const onCloseMenu = () => {
    menuOpen.value = false;
};

const onRemove = async (id: string) => {
    const { isSuccess } = await setNotificationRead(id);
    if (isSuccess) {
        await request();
    }
};
</script>

<template>
    <flat-btn size="1.6rem" icon="las la-bell">
        <floating-badge
            v-if="hasUnreadNotifications"
            rounded
            :label="status?.Unread"
            class="text-bold"
            text-color="on-secondary"
            color="secondary"
            :offset="[16, 8]"
        />
        <q-menu
            v-model="menuOpen"
            ref="menuRef"
            @before-show="onMenuBeforeShow"
            anchor="bottom right"
            self="top right"
            :offset="[0, 19]"
        >
            <q-list style="min-width: 100px; max-width: 600px">
                <q-inner-loading
                    :showing="isLoading && notifications.length > 0"
                    class="q-mb-md"
                    color="primary"
                    size="sm"
                />
                <q-item v-if="isLoading && notifications.length === 0">
                    <q-item-section>
                        <q-inner-loading :showing="isLoading" color="primary" size="sm" />
                    </q-item-section>
                </q-item>
                <div v-else-if="notifications.length === 0">
                    <q-item class="text-bold text-center">
                        <q-item-section>
                            {{ $t('notifications.sections.unread.noData') }}
                        </q-item-section>
                    </q-item>
                    <q-separator />
                </div>

                <div v-for="n in notifications" :key="n.Id">
                    <notification-status-item
                        :notification="n"
                        :actions="getActions(n)"
                        @execute="onCloseMenu"
                        @remove="() => onRemove(n.Id)"
                    />
                    <q-separator />
                </div>
                <q-item>
                    <q-item-section>
                        <link-btn
                            v-close-popup
                            :label="$t('notifications.showAll')"
                            :to="{ name: 'notifications' }"
                            @click="onCloseMenu"
                        />
                    </q-item-section>
                </q-item>
            </q-list>
        </q-menu>
    </flat-btn>
</template>
